import { FC, useEffect, useState } from 'react';

import { css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { validatePasswordAndUpdateStates } from 'src/utils/passwordValidation';

interface Props {
  password: string;
}

const PasswordValidation: FC<Props> = ({ password }) => {
  const [hasSixLetters, setHasSixLetters] = useState<boolean>(false);
  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const theme = useTheme();

  //useEffect with password as dependency, checking and setting proper states by comparing the regex pattern with the password
  useEffect(() => {
    validatePasswordAndUpdateStates(
      password,
      setHasSixLetters,
      setHasUppercase,
      setHasNumber,
      setHasSpecialChar,
    );
  }, [password]);

  const initialStateStyles = css({
    color: theme.palette.info.dark,
  });
  const correctStateStyles = css({
    color: theme.palette.success.main,
  });
  const errorStateStyles = css({
    color: theme.palette.error.main,
  });

  const successIconStyles = css({
    color: theme.palette.success.main,
    transform: 'scale(0.5)',
    marginTop: '-0.25rem',
  });

  const errorIconStyles = css({
    color: theme.palette.error.main,
    transform: 'scale(0.6)',
    marginTop: '-0.25rem',
  });

  const getCorrectStyles = (state: boolean) => {
    if (!password) {
      return initialStateStyles;
    }
    if (state) {
      return correctStateStyles;
    }
    if (!state) {
      return errorStateStyles;
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
    >
      <Grid display="inline-flex" alignItems="center">
        {hasSixLetters && <DoneIcon css={successIconStyles} />}
        {!hasSixLetters && password && <CloseIcon css={errorIconStyles} />}
        <Typography css={getCorrectStyles(hasSixLetters)} variant="caption">
          Passwords must be at least 6 characters long
        </Typography>
      </Grid>
      <Grid display="inline-flex" alignItems="center">
        {hasUppercase && <DoneIcon css={successIconStyles} />}
        {!hasUppercase && password && <CloseIcon css={errorIconStyles} />}
        <Typography css={getCorrectStyles(hasUppercase)} variant="caption">
          Must contain at least one uppercase letter
        </Typography>
      </Grid>
      <Grid display="inline-flex" alignItems="center">
        {hasNumber && <DoneIcon css={successIconStyles} />}
        {!hasNumber && password && <CloseIcon css={errorIconStyles} />}
        <Typography css={getCorrectStyles(hasNumber)} variant="caption">
          Must contain at least one number
        </Typography>
      </Grid>
      <Grid display="inline-flex" alignItems="flex-start">
        {hasSpecialChar && <DoneIcon css={successIconStyles} />}
        {!hasSpecialChar && password && <CloseIcon css={errorIconStyles} />}
        <Typography css={getCorrectStyles(hasSpecialChar)} variant="caption">
          Must contain at least one symbol (@, !, #...)
        </Typography>
      </Grid>
    </Grid>
  );
};

export default PasswordValidation;
